import React, { useState, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import AuthLoadingCard from "./components/AuthLoadingCard";
import AppHeader from "./components/AppHeader";
import Home from "./components/Home";
import { useAuth } from "./AuthContext";
import LoggedOutCard from "./components/LoggedOutCard";
import LoginModal from "./components/LoginModal";
import ListScreen from "./components/ListScreen";
import ReceptionFlow from "./components/ReceptionFlow";
import ReturnFlow from "./components/ReturnFlow";
import SaveOverlay from "./components/SaveOverlay";
import {
  buildReceptionDraft,
  buildReturnDraft,
  hydrateRecordForFlow,
} from "./recordDefaults";
import {
  createVehicle,
  downloadVehicleFocalx,
  getVehicles,
  removeVehicle,
  removeVehiclesBulk,
  updateVehicle,
  getAdminCompanies,
  createAdminCompany,
  updateAdminCompany,
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
} from "./apiClient";

const C = {
  primary: "#00d4aa",
  accent: "#667eea",
  white: "#e8e8e8",
  lightGray: "rgba(255,255,255,0.05)",
  gray: "rgba(255,255,255,0.1)",
  darkGray: "rgba(255,255,255,0.5)",
  text: "#e8e8e8",
  success: "#00d4aa",
  danger: "#ff4757",
};

function isProcessedReturn(record) {
  return (
    record &&
    record.type === "return" &&
    record.returnProcessing &&
    record.returnProcessing.state === "processed"
  );
}

export default function App() {
  const { user, token, isAuthenticated, isAdmin, authLoading, login, logout } =
    useAuth();

  const [page, setPage] = useState("home");
  const [returns, setReturns] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [detailsOrigin, setDetailsOrigin] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editOrigin, setEditOrigin] = useState(null);
  const [search, setSearch] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [dataError, setDataError] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [saveState, setSaveState] = useState({ active: false, message: "" });

  useEffect(() => {
    let active = true;

    async function loadRecords() {
      if (!isAuthenticated || !token) {
        if (active) {
          setReturns([]);
          setReceptions([]);
          setDataError("");
        }
        return;
      }

      try {
        setDataLoading(true);
        const response = await getVehicles(token);
        if (!active) return;
        const items = Array.isArray(response.items) ? response.items : [];
        setReturns(items.filter((item) => item.type === "return"));
        setReceptions(items.filter((item) => item.type === "reception"));
        setDataError("");
      } catch (_error) {
        if (active) {
          setDataError("Kunne ikke laste data fra serveren.");
        }
      } finally {
        if (active) setDataLoading(false);
      }
    }

    loadRecords();

    return () => {
      active = false;
    };
  }, [isAuthenticated, token]);

  const newReturn = () => {
    setCurrent({ id: Date.now(), ...buildReturnDraft() });
    setPage("reg-return");
  };
  const newReception = () => {
    setCurrent({ id: Date.now(), ...buildReceptionDraft() });
    setPage("reg-reception");
  };
  const startEditing = (record, originPage) => {
    setEditing(hydrateRecordForFlow(record));
    setEditOrigin(originPage);
    setPage("edit");
  };

  const completeReturn = async (r) => {
    setSaveState({ active: true, message: "Lagrer retur..." });
    try {
      const result = await createVehicle(token, r);
      setReturns((prev) => [result.item, ...prev]);
      setCurrent(null);
      setPage("home");
    } catch (error) {
      const message = String(error && error.message ? error.message : "");
      if (/Cannot POST\s+\/api\//i.test(message)) {
        alert(
          "Kunne ikke lagre retur: API-endepunktet svarer ikke for POST. Sjekk at backend kjører og at REACT_APP_API_BASE_URL peker til riktig API-base."
        );
      } else {
        alert(message || "Kunne ikke lagre retur.");
      }
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };
  const completeReception = async (r) => {
    setSaveState({ active: true, message: "Lagrer mottak..." });
    try {
      const result = await createVehicle(token, r);
      setReceptions((prev) => [result.item, ...prev]);
      setCurrent(null);
      setPage("home");
    } catch (error) {
      alert(error.message || "Kunne ikke lagre mottak.");
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };
  const del = async (id, t) => {
    if (!isAdmin) return alert("Kun admin");
    if (confirm("Slette?")) {
      try {
        await removeVehicle(token, id);
        if (t === "return") setReturns((prev) => prev.filter((r) => r.id !== id));
        else setReceptions((prev) => prev.filter((r) => r.id !== id));
      } catch (error) {
        alert(error.message || "Kunne ikke slette registrering.");
      }
    }
  };

  const bulkDel = async (ids, t) => {
    if (!isAdmin) return alert("Kun admin");
    const uniqueIds = [...new Set((ids || []).map((id) => String(id || "").trim()).filter(Boolean))];
    if (!uniqueIds.length) return;
    if (!window.confirm(`Slette ${uniqueIds.length} valgte ${t === "return" ? "returnerte" : "mottatte"} biler?`)) return;

    setSaveState({ active: true, message: "Sletter valgte biler..." });
    try {
      const result = await removeVehiclesBulk(token, uniqueIds);
      const deletedIds = Array.isArray(result.deletedIds) && result.deletedIds.length
        ? result.deletedIds
        : uniqueIds;
      if (t === "return") {
        setReturns((prev) => prev.filter((r) => !deletedIds.includes(r.id)));
      } else {
        setReceptions((prev) => prev.filter((r) => !deletedIds.includes(r.id)));
      }
    } catch (error) {
      alert(error.message || "Kunne ikke slette valgte registreringer.");
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };
  const saveEdit = async (r) => {
    setSaveState({ active: true, message: "Lagrer endringer..." });
    try {
      const result = await updateVehicle(token, r.id, r);
      if (r.type === "return") {
        setReturns((prev) => prev.map((x) => (x.id === r.id ? result.item : x)));
      } else {
        setReceptions((prev) =>
          prev.map((x) => (x.id === r.id ? result.item : x))
        );
      }
      if (viewing && viewing.id === r.id) {
        setViewing(result.item);
      }
      setEditing(null);
      if (r.type === "return" && isProcessedReturn(result.item)) {
        setPage("list-processed-returns");
      } else if (r.type === "return" && editOrigin === "list-processed-returns") {
        setPage("list-returns");
      } else {
        setPage(editOrigin || (r.type === "return" ? "list-returns" : "list-receptions"));
      }
      setEditOrigin(null);
    } catch (error) {
      alert(error.message || "Kunne ikke lagre endringer.");
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };
  const reopenReturn = async (record) => {
    if (!isAdmin) return;
    setSaveState({ active: true, message: "Gjenåpner retur..." });
    try {
      const result = await updateVehicle(token, record.id, {
        ...record,
        returnProcessingAction: "reopen",
      });
      setReturns((prev) => prev.map((x) => (x.id === record.id ? result.item : x)));
      if (viewing && viewing.id === record.id) {
        setViewing(result.item);
      }
    } catch (error) {
      alert(error.message || "Kunne ikke gjenåpne retur.");
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };
  const toggleProcessing = async (record, field, value) => {
    const previous = record.processing?.[field];
    const optimistic = {
      ...record,
      processing: { ...(record.processing || {}), [field]: value },
    };
    setReturns((prev) => prev.map((x) => (x.id === record.id ? optimistic : x)));
    if (viewing && viewing.id === record.id) {
      setViewing(optimistic);
    }
    try {
      const result = await updateVehicle(token, record.id, optimistic);
      setReturns((prev) => prev.map((x) => (x.id === record.id ? result.item : x)));
      if (viewing && viewing.id === record.id) {
        setViewing(result.item);
      }
    } catch (error) {
      const reverted = {
        ...record,
        processing: { ...(record.processing || {}), [field]: previous },
      };
      setReturns((prev) => prev.map((x) => (x.id === record.id ? reverted : x)));
      if (viewing && viewing.id === record.id) {
        setViewing(reverted);
      }
      alert(error.message || "Kunne ikke lagre endringer.");
    }
  };
  const flattenForExport = (value, prefix = "", out = {}) => {
    if (value == null) {
      if (prefix) out[prefix] = "";
      return out;
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        if (prefix) out[prefix] = "";
        return out;
      }
      value.forEach((entry, idx) => {
        const path = prefix ? `${prefix}.${idx}` : String(idx);
        flattenForExport(entry, path, out);
      });
      return out;
    }

    if (typeof value === "object") {
      const keys = Object.keys(value);
      if (!keys.length) {
        if (prefix) out[prefix] = "";
        return out;
      }
      keys.forEach((key) => {
        // Exclude image payloads from export for now.
        if (/photo|image/i.test(key)) return;
        const path = prefix ? `${prefix}.${key}` : key;
        flattenForExport(value[key], path, out);
      });
      return out;
    }

    out[prefix] = String(value);
    return out;
  };

  const csvEscape = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const exportCSV = (t) => {
    const source = t === "return" ? returns : receptions;
    const d = source.map((record) => {
      const recordCompany =
        record.companyName ||
        (!isAdmin ? user?.companyName : null) ||
        "Ukjent bedrift";
      const recordUser = record.createdByName || "Ukjent bruker";

      return flattenForExport({
        Company: recordCompany,
        User: recordUser,
        CompanyId: record.companyId || "",
        CreatedById: record.createdBy || "",
        ...record,
      });
    });

    if (!d.length) {
      alert("Ingen data å eksportere.");
      return;
    }

    const headerSet = new Set();
    d.forEach((row) => {
      Object.keys(row).forEach((key) => headerSet.add(key));
    });
    const preferredHeaders = [
      "Company",
      "User",
      "CompanyId",
      "CreatedById",
      "type",
      "basicInfo.regNum",
      "created",
      "updated",
    ];
    const dynamicHeaders = Array.from(headerSet)
      .filter((key) => !preferredHeaders.includes(key))
      .sort((a, b) => a.localeCompare(b));
    const headers = [
      ...preferredHeaders.filter((key) => headerSet.has(key)),
      ...dynamicHeaders,
    ];

    const rows = d.map((row) => headers.map((key) => csvEscape(row[key] || "")));
    const csv = [
      headers.map((h) => csvEscape(h)).join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const companySet = new Set(d.map((row) => row.Company).filter(Boolean));
    const filenameCompany =
      companySet.size === 1
        ? Array.from(companySet)[0]
        : (!isAdmin ? user?.companyName : null) || "mixed";
    const companyForFile = String(filenameCompany)
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "") || "all";
    link.download = `${t}_${companyForFile}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const submitLogin = async () => {
    setSaveState({ active: true, message: "Logger inn..." });
    try {
      await login(email, password);
      setShowLogin(false);
      setEmail("");
      setPassword("");
      setLoginError("");
    } catch (error) {
      setLoginError(error.message || "Feil brukernavn/passord");
    } finally {
      setSaveState({ active: false, message: "" });
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
        fontFamily: "Segoe UI, sans-serif",
        color: C.text,
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .btn-p{background:linear-gradient(135deg,#00d4aa 0%,#00a8cc 100%);color:#0a0a0a;border:none;padding:14px 28px;border-radius:12px;font-weight:600;cursor:pointer;transition:all .2s;box-shadow:0 4px 15px rgba(0,212,170,0.3)}.btn-p:hover{transform:translateY(-2px);box-shadow:0 6px 25px rgba(0,212,170,0.4)}
        .btn-s{background:rgba(255,255,255,0.1);color:#e8e8e8;border:1px solid rgba(255,255,255,0.2);padding:12px 24px;border-radius:12px;font-weight:600;cursor:pointer;backdrop-filter:blur(10px);transition:all .2s}.btn-s:hover{background:rgba(255,255,255,0.2)}
        .btn-d{background:linear-gradient(135deg,#ff4757 0%,#ff3838 100%);color:white;border:none;padding:10px 20px;border-radius:10px;font-weight:600;cursor:pointer}
        .inp{width:100%;padding:14px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:12px;font-size:15px;color:#e8e8e8;backdrop-filter:blur(10px)}.inp:focus{outline:none;border-color:#00d4aa;box-shadow:0 0 0 3px rgba(0,212,170,0.1)}.inp::placeholder{color:rgba(255,255,255,0.4)}
        .card{background:rgba(255,255,255,0.05);border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.1);backdrop-filter:blur(20px)}
        .toggle{position:relative;width:56px;height:28px;background:linear-gradient(135deg,#ff4757 0%,#ff3838 100%);border-radius:14px;cursor:pointer;transition:.3s}.toggle.on{background:linear-gradient(135deg,#00d4aa 0%,#00a8cc 100%)}.toggle::after{content:'';position:absolute;top:3px;left:3px;width:22px;height:22px;background:white;border-radius:50%;transition:.3s;box-shadow:0 2px 5px rgba(0,0,0,0.2)}.toggle.on::after{left:31px}
        .status-ok{background:rgba(0,212,170,0.2);color:#00d4aa;padding:6px 12px;border-radius:20px;font-size:13px;font-weight:600;border:1px solid rgba(0,212,170,0.3)}
        .status-bad{background:rgba(255,71,87,0.2);color:#ff4757;padding:6px 12px;border-radius:20px;font-size:13px;font-weight:600;border:1px solid rgba(255,71,87,0.3)}
        .marker{position:absolute;width:28px;height:28px;background:linear-gradient(135deg,#ff4757 0%,#ff3838 100%);border:3px solid white;border-radius:50%;transform:translate(-50%,-50%);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;color:white;box-shadow:0 4px 15px rgba(255,71,87,0.5);z-index:10}.marker:hover{transform:translate(-50%,-50%) scale(1.15)}
        .glow{background:linear-gradient(135deg,#00d4aa 0%,#00a8cc 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        select{background:rgba(26,26,46,0.9);color:#e8e8e8;border:1px solid rgba(0,212,170,0.3);padding:12px;border-radius:8px;font-size:15px;cursor:pointer}select:focus{outline:none;border-color:#00d4aa;box-shadow:0 0 0 3px rgba(0,212,170,0.1)}
        select option{background:#1a1a2e;color:#e8e8e8;padding:8px}select option:checked{background:#00d4aa;color:#0a0a0a}
        input[type="number"]{-webkit-appearance:textfield;-moz-appearance:textfield;appearance:textfield}input[type="number"]::-webkit-outer-spin-button,input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
        .spinner{width:26px;height:26px;border-radius:50%;border:3px solid rgba(255,255,255,0.25);border-top-color:#00d4aa;animation:spin .8s linear infinite}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
      <AppHeader
        isAdmin={isAdmin}
        isAuthenticated={isAuthenticated}
        user={user}
        onAuthClick={() => {
          if (isAuthenticated) {
            logout();
            setPage("home");
            return;
          }
          setShowLogin(true);
        }}
      />
      {showLogin && !isAuthenticated && (
        <LoginModal
          email={email}
          password={password}
          loginError={loginError}
          saveState={saveState}
          darkGray={C.darkGray}
          danger={C.danger}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={submitLogin}
          onCancel={() => {
            setShowLogin(false);
            setEmail("");
            setPassword("");
            setLoginError("");
          }}
        />
      )}
      <main style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
        {authLoading ? (
          <AuthLoadingCard />
        ) : !isAuthenticated ? (
          <LoggedOutCard
            darkGray={C.darkGray}
            onOpenLogin={() => setShowLogin(true)}
          />
        ) : (
          <>
            {dataError && (
              <div
                className="card"
                style={{ padding: "14px 18px", marginBottom: "20px", color: C.danger }}
              >
                {dataError}
              </div>
            )}
            {dataLoading && (
              <div className="card" style={{ padding: "14px 18px", marginBottom: "20px" }}>
                Oppdaterer data...
              </div>
            )}
            {page === "home" && (
              <Home
                onReturn={newReturn}
                onReception={newReception}
                onViewReturns={() => setPage("list-returns")}
                onViewProcessedReturns={() => setPage("list-processed-returns")}
                onViewReceptions={() => setPage("list-receptions")}
                rc={returns.filter((r) => !isProcessedReturn(r)).length}
                prc={returns.filter((r) => isProcessedReturn(r)).length}
                rec={receptions.length}
                isAdmin={isAdmin}
                onAdmin={() => setPage("admin")}
              />
            )}
            {page === "reg-return" && current && (
              <ReturnFlow
                key={current.id}
                reg={current}
                setReg={setCurrent}
                onDone={completeReturn}
                isSaving={saveState.active}
                C={C}
                DamagesComponent={Damages}
                FocalxSectionComponent={FocalxSection}
                ConfirmReturnComponent={ConfirmReturn}
                showProcessingSection={false}
                onCancel={() => {
                  setCurrent(null);
                  setPage("home");
                }}
              />
            )}
            {page === "reg-reception" && current && (
              <ReceptionFlow
                key={current.id}
                reg={current}
                setReg={setCurrent}
                onDone={completeReception}
                isSaving={saveState.active}
                C={C}
                DamagesComponent={Damages}
                ConfirmReceptionComponent={ConfirmReception}
                onCancel={() => {
                  setCurrent(null);
                  setPage("home");
                }}
              />
            )}
            {page === "list-returns" && (
              <ListScreen
                data={returns
                  .filter((r) => !isProcessedReturn(r))
                  .filter((r) =>
                  r.basicInfo.regNum.toLowerCase().includes(search.toLowerCase())
                )}
                type="return"
                search={search}
                setSearch={setSearch}
                onView={(r) => {
                  setDetailsOrigin("list-returns");
                  setViewing(r);
                  setPage("details");
                }}
                onEdit={(r) => startEditing(r, "list-returns")}
                onDel={(id) => del(id, "return")}
                onBulkDel={(ids) => bulkDel(ids, "return")}
                onBack={() => setPage("home")}
                onExport={() => exportCSV("return")}
                onToggleProcessing={toggleProcessing}
                isAdmin={isAdmin}
                C={C}
              />
            )}
            {page === "list-processed-returns" && (
              <ListScreen
                data={returns
                  .filter((r) => isProcessedReturn(r))
                  .filter((r) =>
                    r.basicInfo.regNum.toLowerCase().includes(search.toLowerCase())
                  )}
                type="return"
                search={search}
                setSearch={setSearch}
                onView={(r) => {
                  setDetailsOrigin("list-processed-returns");
                  setViewing(r);
                  setPage("details");
                }}
                onEdit={(r) => startEditing(r, "list-processed-returns")}
                onDel={(id) => del(id, "return")}
                onBulkDel={(ids) => bulkDel(ids, "return")}
                onBack={() => setPage("home")}
                onExport={() => exportCSV("return")}
                onToggleProcessing={toggleProcessing}
                onReopen={isAdmin ? reopenReturn : null}
                titleOverride="Behandlede returer"
                isAdmin={isAdmin}
                C={C}
              />
            )}
            {page === "list-receptions" && (
              <ListScreen
                data={receptions.filter((r) =>
                  r.basicInfo.regNum.toLowerCase().includes(search.toLowerCase())
                )}
                type="reception"
                search={search}
                setSearch={setSearch}
                onView={(r) => {
                  setDetailsOrigin("list-receptions");
                  setViewing(r);
                  setPage("details");
                }}
                onEdit={(r) => startEditing(r, "list-receptions")}
                onDel={(id) => del(id, "reception")}
                onBulkDel={(ids) => bulkDel(ids, "reception")}
                onBack={() => setPage("home")}
                onExport={() => exportCSV("reception")}
                isAdmin={isAdmin}
                C={C}
              />
            )}
            {page === "details" && viewing && (
              <Details
                reg={viewing}
                token={token}
                onBack={() => {
                  setViewing(null);
                  setPage(detailsOrigin || (viewing.type === "return" ? "list-returns" : "list-receptions"));
                  setDetailsOrigin(null);
                }}
                onEdit={() => {
                  startEditing(viewing, detailsOrigin || "details");
                }}
                onToggleProcessing={toggleProcessing}
              />
            )}
            {page === "edit" && editing && (
              editing.type === "return" ? (
                <ReturnFlow
                  key={editing.id}
                  reg={editing}
                  setReg={setEditing}
                  onDone={saveEdit}
                  isSaving={saveState.active}
                  C={C}
                  DamagesComponent={Damages}
                  FocalxSectionComponent={FocalxSection}
                  ConfirmReturnComponent={ConfirmReturn}
                  showProcessingSection={true}
                  onCancel={() => {
                    setEditing(null);
                    setPage(editOrigin || "list-returns");
                    setEditOrigin(null);
                  }}
                />
              ) : (
                <ReceptionFlow
                  key={editing.id}
                  reg={editing}
                  setReg={setEditing}
                  onDone={saveEdit}
                  isSaving={saveState.active}
                  C={C}
                  DamagesComponent={Damages}
                  ConfirmReceptionComponent={ConfirmReception}
                  onCancel={() => {
                    setEditing(null);
                    setPage(editOrigin || "list-receptions");
                    setEditOrigin(null);
                  }}
                />
              )
            )}
            {page === "admin" && isAdmin && (
              <AdminPanel
                token={token}
                onBack={() => setPage("home")}
              />
            )}
          </>
        )}
      </main>
      {saveState.active && (
        <SaveOverlay message={saveState.message} />
      )}
      <Analytics />
    </div>
  );
}

function FocalxSection({ reg, setReg }) {
  const [error, setError] = useState("");

  const onPickPdf = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Kun PDF-filer er tillatt.");
      event.target.value = "";
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setError("PDF er for stor. Maks størrelse er 8 MB.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setError("");
      setReg({
        ...reg,
        focalxPending: {
          fileName: file.name,
          mimeType: "application/pdf",
          dataUrl: String(ev.target.result || ""),
        },
      });
      event.target.value = "";
    };
    reader.onerror = () => {
      setError("Kunne ikke lese PDF-filen.");
      event.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      style={{
        padding: "24px",
        borderRadius: "14px",
        border: "1px solid rgba(255,255,255,0.1)",
        background: "rgba(255,255,255,0.03)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: "10px", color: C.primary }}>
        Focalx rapport (valgfritt)
      </h3>
      <p style={{ marginTop: 0, color: C.darkGray, marginBottom: "14px" }}>
        Last opp PDF fra Focalx. Filen lagres sammen med registreringen ved fullføring.
      </p>
      <input
        type="file"
        id="focalx-upload-input"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={onPickPdf}
      />
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <label htmlFor="focalx-upload-input" className="btn-s" style={{ padding: "10px 16px" }}>
          Velg PDF
        </label>
        {reg.focalxPending && (
          <>
            <span className="status-ok">{reg.focalxPending.fileName}</span>
            <button
              className="btn-d"
              style={{ padding: "8px 14px", fontSize: "13px" }}
              onClick={() => setReg({ ...reg, focalxPending: null })}
            >
              Fjern
            </button>
          </>
        )}
        {!reg.focalxPending && reg.focalx && (
          <span className="status-ok">Lagrert: {reg.focalx.fileName}</span>
        )}
      </div>
      {error && (
        <p style={{ marginBottom: 0, marginTop: "12px", color: C.danger }}>{error}</p>
      )}
    </div>
  );
}

function Damages({ reg, setReg }) {
  const [view, setView] = useState("left");
  const [uploadBusyId, setUploadBusyId] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const views = [
    { id: "front", l: "Foran" },
    { id: "back", l: "Bak" },
    { id: "left", l: "Venstre" },
    { id: "right", l: "Høyre" },
    { id: "top", l: "Over" },
  ];
  const types = ["Bulk", "Ripe", "Sprekk", "Normal slitasje"];
  const addDamage = () => {
    setReg({
      ...reg,
      damages: [
        ...reg.damages,
        { id: Date.now(), view, desc: "", type: "Ripe", photo: null },
      ],
    });
  };
  const updD = (id, f, v) =>
    setReg({
      ...reg,
      damages: reg.damages.map((d) => (d.id === id ? { ...d, [f]: v } : d)),
    });
  const remD = (id) =>
    setReg({ ...reg, damages: reg.damages.filter((d) => d.id !== id) });
  const dataUrlBytes = (dataUrl) => {
    const comma = dataUrl.indexOf(",");
    if (comma === -1) return 0;
    const b64 = dataUrl.slice(comma + 1);
    const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
    return Math.floor((b64.length * 3) / 4) - padding;
  };
  const photo = (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploadError("");
    setUploadBusyId(id);
    const r = new FileReader();
    r.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        let quality = 0.75;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        const MAX_BYTES = 900 * 1024;
        while (dataUrlBytes(dataUrl) > MAX_BYTES && quality > 0.4) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        if (dataUrlBytes(dataUrl) > MAX_BYTES) {
          setUploadError("Bildet er for stort. Velg et mindre bilde.");
          updD(id, "photo", null);
        } else {
          updD(id, "photo", dataUrl);
        }
        setUploadBusyId(null);
      };
      img.onerror = () => {
        setUploadError("Kunne ikke lese bildet. Prøv en annen fil.");
        setUploadBusyId(null);
      };
      img.src = ev.target.result;
    };
    r.onerror = () => {
      setUploadError("Kunne ikke lese filen. Prøv igjen.");
      setUploadBusyId(null);
    };
    r.readAsDataURL(f);
    e.target.value = "";
  };

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "16px", color: C.primary }}>
        Skaderegistrering
      </h2>
      <p style={{ color: C.darkGray, marginBottom: "24px" }}>
        Velg side av bilen og legg til skader
      </p>
      {uploadError && (
        <div
          style={{
            marginBottom: "16px",
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid rgba(255,71,87,0.4)",
            background: "rgba(255,71,87,0.15)",
            color: "#ff9aa4",
            fontSize: "14px",
          }}
        >
          {uploadError}
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "24px",
          flexWrap: "wrap",
        }}
      >
        {views.map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              background:
                view === v.id
                  ? "linear-gradient(135deg, #00d4aa 0%, #00a8cc 100%)"
                  : "rgba(255,255,255,0.1)",
              color: view === v.id ? "#0a0a0a" : C.text,
              cursor: "pointer",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            {v.l}
            {reg.damages.filter((d) => d.view === v.id).length > 0 && (
              <span
                style={{
                  marginLeft: "8px",
                  background: view === v.id ? "rgba(0,0,0,.2)" : C.danger,
                  padding: "2px 8px",
                  borderRadius: "10px",
                  fontSize: "12px",
                  color: "white",
                }}
              >
                {reg.damages.filter((d) => d.view === v.id).length}
              </span>
            )}
          </button>
        ))}
      </div>
      <button
        onClick={addDamage}
        className="btn-p"
        style={{
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        + Legg til skade ({views.find((v) => v.id === view)?.l})
      </button>
      {reg.damages.length > 0 && (
        <div>
          <h3 style={{ marginBottom: "16px", color: C.primary }}>
            Registrerte skader ({reg.damages.length})
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {reg.damages.map((d, i) => (
              <div
                key={d.id}
                style={{
                  padding: "20px",
                  background: "rgba(255,71,87,0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,71,87,0.2)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <span
                    style={{
                      width: "28px",
                      height: "28px",
                      background:
                        "linear-gradient(135deg, #ff4757 0%, #ff3838 100%)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "white",
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontWeight: "600" }}>
                    Skade {i + 1} - {views.find((v) => v.id === d.view)?.l}
                  </span>
                  <button
                    onClick={() => remD(d.id)}
                    style={{
                      marginLeft: "auto",
                      padding: "6px 12px",
                      background: "transparent",
                      border: "1px solid #ff4757",
                      borderRadius: "6px",
                      color: "#ff4757",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Fjern
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "rgba(255,255,255,0.7)",
                      }}
                    >
                      Skadetype
                    </label>
                    <select
                      value={d.type}
                      onChange={(e) => updD(d.id, "type", e.target.value)}
                      className="inp"
                      style={{ padding: "10px" }}
                    >
                      {types.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "rgba(255,255,255,0.7)",
                      }}
                    >
                      Bilde (valgfritt)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => photo(d.id, e)}
                      style={{ display: "none" }}
                      id={`p${d.id}`}
                    />
                    <label
                      htmlFor={`p${d.id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px 16px",
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "8px",
                        cursor: uploadBusyId === d.id ? "wait" : "pointer",
                        fontSize: "14px",
                        opacity: uploadBusyId === d.id ? 0.75 : 1,
                      }}
                    >
                      {uploadBusyId === d.id ? "⏳ Behandler bilde..." : `📷 ${d.photo ? "✓ Lastet opp" : "Velg bilde"}`}
                    </label>
                  </div>
                </div>
                {d.photo && (
                  <img
                    src={d.photo}
                    alt=""
                    style={{
                      marginTop: "12px",
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                )}
                <div style={{ marginTop: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                      fontSize: "14px",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    Beskrivelse av skade
                  </label>
                  <textarea
                    value={d.desc}
                    onChange={(e) => updD(d.id, "desc", e.target.value)}
                    placeholder="Beskriv skaden her..."
                    className="inp"
                    rows={2}
                    style={{ padding: "10px" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {reg.damages.length === 0 && (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "12px",
            border: "2px dashed rgba(255,255,255,0.15)",
          }}
        >
          <p style={{ color: C.darkGray, margin: 0 }}>
            Ingen skader registrert. Klikk "Legg til skade" for å registrere.
          </p>
        </div>
      )}
    </div>
  );
}

function ConfirmReception({ reg }) {
  const basicInfo = reg.basicInfo || {};
  const checklist = reg.checklist || {};
  const damages = Array.isArray(reg.damages) ? reg.damages : [];
  const lbl = {
    tollTag: "Bombrikke",
    rubberMats: "Gummimatter",
    fullTank: "Full tank/Fulladet",
    shelf: "Rollo/Hattehylle",
    cableType2: "Ladekabel type 2",
    emergencyCharger: "Ladekabel nødlader",
    twoTireSets: "Mottatt 2 dekksett",
    noDamage: "Fri for skader",
  };
  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "24px", color: C.primary }}>
        Bekreft
      </h2>
      <div style={{ display: "grid", gap: "20px" }}>
        <div
          style={{
            padding: "20px",
            background: `${C.success}10`,
            borderRadius: "12px",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.success }}>Info</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <b>Regnr:</b> {basicInfo.regNum || "-"}
            </div>
            <div>
              <b>Dato:</b> {basicInfo.date || "-"}
            </div>
            <div>
              <b>KM:</b> {basicInfo.mileage || "-"}
            </div>
            <div>
              <b>Av:</b> {basicInfo.by || "-"}
            </div>
            <div>
              <b>Lokasjon:</b> {basicInfo.location || "-"}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "20px",
            background: C.lightGray,
            borderRadius: "12px",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.primary }}>Sjekk</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
            }}
          >
            {Object.entries(checklist).map(([k, v]) => (
              <div
                key={k}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: v ? C.success : C.danger,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "white",
                  }}
                >
                  {v ? "✓" : "✗"}
                </span>
                <span style={{ fontSize: "14px" }}>{lbl[k]}</span>
              </div>
            ))}
          </div>
          {reg.notes && (
            <div style={{ marginTop: "16px" }}>
              <b>Kommentar:</b> {reg.notes}
            </div>
          )}
        </div>
        {damages.length > 0 && (
          <div
            style={{
              padding: "20px",
              background: `${C.danger}10`,
              borderRadius: "12px",
            }}
          >
            <h3 style={{ margin: "0 0 16px", color: C.danger }}>
              Skader: {damages.length}
            </h3>
            {damages.map((d, i) => (
              <div key={d.id} style={{ fontSize: "14px", marginBottom: "8px" }}>
                <b>{i + 1}:</b> {d.type} - {d.desc || "-"}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmReturn({ reg }) {
  const basicInfo = reg.basicInfo || {};
  const checklist = reg.checklist || {};
  const returnInfo = reg.returnInfo || {};
  const damages = Array.isArray(reg.damages) ? reg.damages : [];
  const focalx = reg.focalx || null;
  const pendingFocalx = reg.focalxPending || null;
  const focalxLabel = focalx
    ? `✓ ${focalx.fileName}`
    : pendingFocalx
      ? `✓ ${pendingFocalx.fileName} (klar til opplasting ved lagring)`
      : "Ingen fil lastet opp";
  const focalxOk = Boolean(focalx || pendingFocalx);
  const lbl = {
    tollTag: "Bombrikke/belastet",
    spareKey: "Reservenøkkel",
    twoTireSets: "To dekksett",
    shelf: "Hattehylle/Rollo",
    emergencyCharger: "Nødlader",
    cableType2: "Ladekabel type 2",
    tank: "Full tank",
  };
  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "24px", color: C.primary }}>
        Bekreft
      </h2>
      <div style={{ display: "grid", gap: "20px" }}>
        <div
          style={{
            padding: "20px",
            background: `${C.accent}15`,
            borderRadius: "12px",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.primary }}>Kjøretøy</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <b>Regnr:</b> {basicInfo.regNum || "-"}
            </div>
            <div>
              <b>Type:</b> {basicInfo.vehicleType || "-"}
            </div>
            <div>
              <b>Merke:</b> {basicInfo.brand || "-"}
            </div>
            <div>
              <b>Modell:</b> {basicInfo.model || "-"}
            </div>
            <div>
              <b>KM:</b> {basicInfo.mileage || "-"}
            </div>
            <div>
              <b>Lokasjon:</b> {basicInfo.location || "-"}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "20px",
            background: C.lightGray,
            borderRadius: "12px",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.primary }}>Sjekk</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
            }}
          >
            {Object.entries(checklist).map(([k, v]) => (
              <div
                key={k}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    background: v ? C.success : C.danger,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "white",
                  }}
                >
                  {v ? "✓" : "✗"}
                </span>
                <span style={{ fontSize: "13px" }}>{lbl[k]}</span>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            padding: "20px",
            background: C.lightGray,
            borderRadius: "12px",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.primary }}>Retur</h3>
          <div>
            <b>Dato:</b> {returnInfo.date || "-"}
          </div>
          <div>
            <b>Av:</b> {returnInfo.by || "-"}
          </div>
          {returnInfo.notes && (
            <div style={{ marginTop: "12px" }}>
            <b>Notat:</b> {returnInfo.notes}
            </div>
          )}
        </div>
        <div
          style={{
            padding: "20px",
          background: damages.length ? `${C.danger}10` : `${C.success}10`,
            borderRadius: "12px",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px",
            color: damages.length ? C.danger : C.success,
            }}
          >
          Skader: {damages.length || "Ingen"}
          </h3>
        {damages.map((d, i) => (
            <div key={d.id} style={{ fontSize: "14px", marginBottom: "8px" }}>
              <b>{i + 1}:</b> {d.type} - {d.desc || "-"}
            </div>
          ))}
        </div>
        <div
          style={{
            padding: "20px",
            background: focalxOk ? `${C.success}10` : `${C.danger}10`,
            borderRadius: "12px",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px",
              color: focalxOk ? C.success : C.danger,
            }}
          >
            Focalx rapport
          </h3>
          <p style={{ margin: 0 }}>{focalxLabel}</p>
        </div>
      </div>
    </div>
  );
}

export function Details({ reg, token, onBack, onEdit, onToggleProcessing }) {
  const isRet = reg.type === "return";
  const basicInfo = reg.basicInfo || {};
  const returnInfo = reg.returnInfo || {};
  const damages = Array.isArray(reg.damages) ? reg.damages : [];
  const focalx = reg.focalx || null;
  const [processingSaving, setProcessingSaving] = useState({});
  const handleToggleProcessing = async (record, field, value) => {
    if (!onToggleProcessing) return;
    setProcessingSaving((prev) => ({ ...prev, [field]: true }));
    try {
      await onToggleProcessing(record, field, value);
    } finally {
      setProcessingSaving((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };
  const vl = {
    front: "Foran",
    back: "Bak",
    left: "Venstre",
    right: "Høyre",
    top: "Over",
  };
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <button onClick={onBack} className="btn-s">
          ← Tilbake
        </button>
        <button onClick={onEdit} className="btn-p">
          Rediger
        </button>
      </div>
      <div className="card" style={{ padding: "40px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginBottom: "32px",
            paddingBottom: "24px",
            borderBottom: `1px solid ${C.gray}`,
          }}
        >
          <div
            style={{
              width: "70px",
              height: "70px",
              background: `${C.accent}15`,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "36px",
            }}
          >
            🚗
          </div>
          <div>
            <h1
              style={{ margin: "0 0 8px", fontSize: "28px", color: C.primary }}
            >
              {basicInfo.regNum || reg.reg_num || "Ukjent"}
            </h1>
            <p style={{ margin: 0, color: C.darkGray }}>
              {isRet
                ? `${basicInfo.brand || ""} ${basicInfo.model || ""}`.trim() ||
                  "Retur"
                : `Mottatt: ${basicInfo.date || "-"}`}
            </p>
          </div>
          <span
            className={isRet ? (focalx ? "status-ok" : "status-bad") : (damages.length ? "status-bad" : "status-ok")}
            style={{ marginLeft: "auto" }}
          >
            {isRet ? (focalx ? "Focalx ✓" : "Ingen Focalx") : (damages.length ? `${damages.length} skade(r)` : "OK")}
          </span>
        </div>
        <div style={{ display: "grid", gap: "24px" }}>
          <div
            style={{
              padding: "20px",
              background: C.lightGray,
              borderRadius: "12px",
            }}
          >
            <h3 style={{ margin: "0 0 16px", color: C.primary }}>Info</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <b>Regnr:</b> {basicInfo.regNum || reg.reg_num || "Ukjent"}
              </div>
              {isRet ? (
                <>
                  <div>
                    <b>Type:</b> {basicInfo.vehicleType || "-"}
                  </div>
                  <div>
                    <b>Merke:</b> {basicInfo.brand || "-"}
                  </div>
                  <div>
                    <b>Modell:</b> {basicInfo.model || "-"}
                  </div>
                  <div>
                    <b>KM:</b> {basicInfo.mileage || "-"}
                  </div>
                  <div>
                    <b>Lokasjon:</b> {basicInfo.location || "-"}
                  </div>
                  <div>
                    <b>Retur:</b> {returnInfo.date || "-"}
                  </div>
                  <div>
                    <b>Av:</b> {returnInfo.by || "-"}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <b>Dato:</b> {basicInfo.date || "-"}
                  </div>
                  <div>
                    <b>KM:</b> {basicInfo.mileage || "-"}
                  </div>
                  <div>
                    <b>Av:</b> {basicInfo.by || "-"}
                  </div>
                  <div>
                    <b>Lokasjon:</b> {basicInfo.location || "-"}
                  </div>
                </>
              )}
            </div>
            {isRet && returnInfo.notes && (
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: `1px solid ${C.gray}`,
                }}
              >
                <b>Notat:</b> {returnInfo.notes}
              </div>
            )}
            {!isRet && reg.notes && (
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: `1px solid ${C.gray}`,
                }}
              >
                <b>Kommentar:</b> {reg.notes}
              </div>
            )}
          </div>
          {isRet && (
            <div
              style={{
                padding: "20px",
                background: C.lightGray,
                borderRadius: "12px",
              }}
            >
              <h3 style={{ margin: "0 0 16px", color: C.primary }}>Behandling</h3>
              <div style={{ display: "flex", gap: "20px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!reg.processing?.autoplanDone}
                    disabled={!!processingSaving.autoplanDone}
                    onChange={(e) =>
                      handleToggleProcessing(reg, "autoplanDone", e.target.checked)
                    }
                  />
                  Autoplan
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!reg.processing?.autofleksDone}
                    disabled={!!processingSaving.autofleksDone}
                    onChange={(e) =>
                      handleToggleProcessing(reg, "autofleksDone", e.target.checked)
                    }
                  />
                  Autofleks
                </label>
              </div>
            </div>
          )}
          {damages.length > 0 && (
            <div
              style={{
                padding: "20px",
                background: `${C.danger}08`,
                borderRadius: "12px",
                border: `1px solid ${C.danger}20`,
              }}
            >
              <h3 style={{ margin: "0 0 16px", color: C.danger }}>
                Skader ({damages.length})
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {damages.map((d, i) => (
                  <div
                    key={d.id || `d-${i}`}
                    style={{
                      padding: "16px",
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                      border: `1px solid ${C.gray}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "28px",
                          height: "28px",
                          background: C.danger,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "white",
                        }}
                      >
                        {i + 1}
                      </span>
                      <span style={{ fontWeight: "600" }}>
                        Skade - {vl[d.view]}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          padding: "4px 12px",
                          background: C.lightGray,
                          borderRadius: "20px",
                          fontSize: "13px",
                        }}
                      >
                        {d.type}
                      </span>
                    </div>
                    {d.desc && (
                      <p style={{ margin: "8px 0 0 40px", color: C.darkGray }}>
                        {d.desc}
                      </p>
                    )}
                    {d.photo && (
                      <img
                        src={d.photo}
                        alt=""
                        style={{
                          marginTop: "12px",
                          marginLeft: "40px",
                          maxWidth: "200px",
                          borderRadius: "8px",
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {isRet && (
            <div
              style={{
                padding: "20px",
                background: focalx ? `${C.success}10` : `${C.danger}10`,
                borderRadius: "12px",
              }}
            >
              <h3 style={{ margin: "0 0 16px", color: focalx ? C.success : C.danger }}>
                Focalx rapport
              </h3>
              {focalx ? (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <span className="status-ok">{focalx.fileName}</span>
                  <button
                    className="btn-p"
                    style={{ padding: "10px 16px", fontSize: "14px" }}
                    onClick={async () => {
                      try {
                        await downloadVehicleFocalx(token, reg.id, focalx.fileName || "focalx.pdf");
                      } catch (error) {
                        alert(error.message || "Kunne ikke laste ned Focalx-PDF.");
                      }
                    }}
                  >
                    Last ned PDF
                  </button>
                </div>
              ) : (
                <p style={{ margin: 0 }}>Ingen Focalx-fil registrert.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ token, onBack }) {
  const EMPTY_COMPANY_DRAFT = {
    name: "",
    orgNumber: "",
    contactName: "",
    contactEmail: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    notes: "",
  };

  const [tab, setTab] = useState("companies");
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companyDraft, setCompanyDraft] = useState(EMPTY_COMPANY_DRAFT);
  const [companyDetailError, setCompanyDetailError] = useState("");
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);

  const [newCompanyName, setNewCompanyName] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);

  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    fullName: "",
    password: "",
    companyId: "",
    role: "user",
  });
  const [userError, setUserError] = useState("");
  const [savingUser, setSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState("");
  const [editUserDraft, setEditUserDraft] = useState({
    email: "",
    fullName: "",
    companyId: "",
    role: "user",
    password: "",
  });
  const [editUserError, setEditUserError] = useState("");
  const [savingEditUser, setSavingEditUser] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [cr, ur, vr] = await Promise.all([
          getAdminCompanies(token),
          getAdminUsers(token),
          getVehicles(token),
        ]);
        if (!active) return;
        setCompanies(cr.items || []);
        setUsers(ur.items || []);
        setVehicles(vr.items || []);
      } catch (err) {
        if (active) setError(err.message || "Kunne ikke laste data");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setCompanyDraft(EMPTY_COMPANY_DRAFT);
      setCompanyDetailError("");
      return;
    }

    const selected = companies.find((company) => company.id === selectedCompanyId);
    if (!selected) {
      setSelectedCompanyId("");
      setCompanyDraft(EMPTY_COMPANY_DRAFT);
      setCompanyDetailError("");
      return;
    }

    setCompanyDraft({
      name: selected.name || "",
      orgNumber: selected.org_number || "",
      contactName: selected.contact_name || "",
      contactEmail: selected.contact_email || "",
      phone: selected.phone || "",
      addressLine1: selected.address_line1 || "",
      addressLine2: selected.address_line2 || "",
      postalCode: selected.postal_code || "",
      city: selected.city || "",
      notes: selected.notes || "",
    });
  }, [companies, selectedCompanyId]);

  const selectedCompany = selectedCompanyId
    ? companies.find((company) => company.id === selectedCompanyId) || null
    : null;
  const selectedCompanyUsers = selectedCompany
    ? users.filter((user) => user.company_id === selectedCompany.id)
    : [];
  const selectedCompanyVehicles = selectedCompany
    ? vehicles.filter((vehicle) => vehicle.companyId === selectedCompany.id)
    : [];

  async function handleAddCompany() {
    if (!newCompanyName.trim()) {
      setCompanyError("Navn er påkrevd");
      return;
    }
    setSavingCompany(true);
    setCompanyError("");
    try {
      const res = await createAdminCompany(token, newCompanyName.trim());
      setCompanies((prev) => [
        ...prev,
        { ...res.item, user_count: 0, vehicle_count: 0 },
      ]);
      setNewCompanyName("");
    } catch (err) {
      setCompanyError(err.message || "Kunne ikke opprette bedrift");
    } finally {
      setSavingCompany(false);
    }
  }

  function openCompany(companyId) {
    setSelectedCompanyId(companyId);
    setCompanyDetailError("");
  }

  async function handleUpdateCompany() {
    if (!selectedCompany) return;
    if (!companyDraft.name.trim()) {
      setCompanyDetailError("Navn er påkrevd");
      return;
    }

    setSavingCompanyEdit(true);
    setCompanyDetailError("");
    try {
      const res = await updateAdminCompany(token, selectedCompany.id, {
        name: companyDraft.name.trim(),
        orgNumber: companyDraft.orgNumber,
        contactName: companyDraft.contactName,
        contactEmail: companyDraft.contactEmail,
        phone: companyDraft.phone,
        addressLine1: companyDraft.addressLine1,
        addressLine2: companyDraft.addressLine2,
        postalCode: companyDraft.postalCode,
        city: companyDraft.city,
        notes: companyDraft.notes,
      });
      setCompanies((prev) =>
        prev.map((company) =>
          company.id === selectedCompany.id ? { ...company, ...res.item } : company
        )
      );
      setUsers((prev) =>
        prev.map((user) =>
          user.company_id === selectedCompany.id
            ? { ...user, company_name: res.item.name }
            : user
        )
      );
    } catch (err) {
      setCompanyDetailError(err.message || "Kunne ikke oppdatere bedrift");
    } finally {
      setSavingCompanyEdit(false);
    }
  }

  function startEditUser(user) {
    setEditingUserId(user.id);
    setEditUserDraft({
      email: user.email || "",
      fullName: user.full_name || "",
      companyId: user.company_id || "",
      role: user.role || "user",
      password: "",
    });
    setEditUserError("");
  }

  function cancelEditUser() {
    setEditingUserId("");
    setEditUserDraft({
      email: "",
      fullName: "",
      companyId: "",
      role: "user",
      password: "",
    });
    setEditUserError("");
  }

  async function handleUpdateUser(id) {
    if (!editUserDraft.email.trim() || !editUserDraft.fullName.trim() || !editUserDraft.companyId) {
      setEditUserError("E-post, navn og bedrift er påkrevd");
      return;
    }

    setSavingEditUser(true);
    setEditUserError("");
    try {
      const res = await updateAdminUser(token, id, {
        email: editUserDraft.email.trim().toLowerCase(),
        fullName: editUserDraft.fullName.trim(),
        companyId: editUserDraft.companyId,
        role: editUserDraft.role,
        password: editUserDraft.password,
      });
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...res.item } : u)));
      cancelEditUser();
    } catch (err) {
      setEditUserError(err.message || "Kunne ikke oppdatere bruker");
    } finally {
      setSavingEditUser(false);
    }
  }

  async function handleAddUser() {
    if (
      !newUser.email.trim() ||
      !newUser.fullName.trim() ||
      !newUser.password ||
      !newUser.companyId
    ) {
      setUserError("Alle felt er påkrevd");
      return;
    }
    setSavingUser(true);
    setUserError("");
    try {
      const res = await createAdminUser(token, {
        email: newUser.email.trim().toLowerCase(),
        fullName: newUser.fullName.trim(),
        password: newUser.password,
        companyId: newUser.companyId,
        role: newUser.role,
      });
      const company = companies.find((c) => c.id === newUser.companyId);
      setUsers((prev) => [
        ...prev,
        { ...res.item, company_name: company?.name || "" },
      ]);
      setNewUser({
        email: "",
        fullName: "",
        password: "",
        companyId: "",
        role: "user",
      });
      setShowAddUser(false);
    } catch (err) {
      setUserError(err.message || "Kunne ikke opprette bruker");
    } finally {
      setSavingUser(false);
    }
  }

  async function handleDeleteUser(id, name) {
    if (!window.confirm(`Slette brukeren «${name}»?`)) return;
    try {
      await deleteAdminUser(token, id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      alert(err.message || "Kunne ikke slette bruker");
    }
  }

  const tabStyle = (active) => ({
    padding: "12px 28px",
    borderRadius: "10px",
    border: "none",
    background: active
      ? "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)"
      : "rgba(255,255,255,0.08)",
    color: active ? "#0a0a0a" : C.text,
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "15px",
    transition: "all 0.2s",
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <button onClick={onBack} className="btn-s">
          ← Tilbake
        </button>
        <h2
          style={{
            margin: 0,
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "26px",
          }}
        >
          ⚙️ Administrasjon
        </h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            style={tabStyle(tab === "companies")}
            onClick={() => setTab("companies")}
          >
            Bedrifter
          </button>
          <button
            style={tabStyle(tab === "users")}
            onClick={() => setTab("users")}
          >
            Brukere
          </button>
        </div>
      </div>

      {error && (
        <div
          className="card"
          style={{ padding: "14px 18px", marginBottom: "20px", color: C.danger }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: "48px", textAlign: "center" }}>
          Laster inn...
        </div>
      ) : tab === "companies" ? (
        <div>
          {selectedCompany && (
            <div
              className="card"
              style={{ padding: "28px", marginBottom: "24px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "16px",
                  flexWrap: "wrap",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <div style={{ color: C.darkGray, fontSize: "13px" }}>
                    Firmaside
                  </div>
                  <h3 style={{ margin: "4px 0 0", color: C.primary, fontSize: "22px" }}>
                    {selectedCompany.name}
                  </h3>
                  <div style={{ color: C.darkGray, fontSize: "13px", marginTop: "6px" }}>
                    Opprettet: {new Date(selectedCompany.created_at).toLocaleDateString("nb-NO")}
                  </div>
                </div>
                <button
                  className="btn-s"
                  onClick={() => setSelectedCompanyId("")}
                >
                  Tilbake til bedrifter
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr",
                  gap: "20px",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    borderRadius: "14px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <h4 style={{ margin: "0 0 14px", color: C.primary }}>
                    Rediger firma
                  </h4>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
                    Firmanavn
                  </label>
                  <input
                    value={companyDraft.name}
                    onChange={(e) => setCompanyDraft({ ...companyDraft, name: e.target.value })}
                    className="inp"
                    placeholder="Firmanavn"
                  />
                  <div
                    style={{
                      marginTop: "10px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                    }}
                  >
                    <input
                      value={companyDraft.orgNumber}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, orgNumber: e.target.value })}
                      className="inp"
                      placeholder="Org.nr"
                    />
                    <input
                      value={companyDraft.contactName}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, contactName: e.target.value })}
                      className="inp"
                      placeholder="Kontaktperson"
                    />
                    <input
                      value={companyDraft.contactEmail}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, contactEmail: e.target.value })}
                      className="inp"
                      placeholder="Kontakt e-post"
                    />
                    <input
                      value={companyDraft.phone}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, phone: e.target.value })}
                      className="inp"
                      placeholder="Telefon"
                    />
                    <input
                      value={companyDraft.addressLine1}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, addressLine1: e.target.value })}
                      className="inp"
                      placeholder="Adresse linje 1"
                    />
                    <input
                      value={companyDraft.addressLine2}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, addressLine2: e.target.value })}
                      className="inp"
                      placeholder="Adresse linje 2"
                    />
                    <input
                      value={companyDraft.postalCode}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, postalCode: e.target.value })}
                      className="inp"
                      placeholder="Postnummer"
                    />
                    <input
                      value={companyDraft.city}
                      onChange={(e) => setCompanyDraft({ ...companyDraft, city: e.target.value })}
                      className="inp"
                      placeholder="By"
                    />
                  </div>
                  <textarea
                    value={companyDraft.notes}
                    onChange={(e) => setCompanyDraft({ ...companyDraft, notes: e.target.value })}
                    className="inp"
                    placeholder="Notater"
                    rows={3}
                    style={{ marginTop: "10px" }}
                  />
                  {companyDetailError && (
                    <p style={{ color: C.danger, margin: "10px 0 0" }}>
                      {companyDetailError}
                    </p>
                  )}
                  <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <button
                      className="btn-p"
                      onClick={handleUpdateCompany}
                      disabled={savingCompanyEdit}
                    >
                      {savingCompanyEdit ? "Lagrer..." : "Lagre endringer"}
                    </button>
                    <button
                      className="btn-s"
                      onClick={() => setTab("users")}
                    >
                      Se brukere
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    padding: "20px",
                    borderRadius: "14px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <h4 style={{ margin: "0 0 14px", color: C.primary }}>
                    Oversikt
                  </h4>
                  <div style={{ display: "grid", gap: "10px" }}>
                    <div className="status-ok">👤 {selectedCompany.user_count} bruker{selectedCompany.user_count !== 1 ? "e" : ""}</div>
                    <div
                      style={{
                        background: "rgba(102,126,234,0.2)",
                        color: "#667eea",
                        padding: "10px 12px",
                        borderRadius: "12px",
                        fontSize: "13px",
                        fontWeight: "600",
                        border: "1px solid rgba(102,126,234,0.3)",
                      }}
                    >
                      🚗 {selectedCompany.vehicle_count} kjøretøy
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "20px" }}>
                <h4 style={{ margin: "0 0 14px", color: C.primary }}>
                  Brukere i firmaet
                </h4>
                {selectedCompanyUsers.length === 0 ? (
                  <div
                    style={{
                      padding: "18px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.03)",
                      color: C.darkGray,
                    }}
                  >
                    Ingen brukere er tilknyttet dette firmaet ennå.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {selectedCompanyUsers.map((user) => (
                      <div
                        key={user.id}
                        className="card"
                        style={{
                          padding: "14px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{user.full_name}</div>
                          <div style={{ color: C.darkGray, fontSize: "13px", marginTop: "2px" }}>
                            {user.email} · {user.role === "admin" ? "Admin" : "Bruker"}
                          </div>
                        </div>
                        <span
                          className="status-ok"
                          style={{ cursor: "pointer" }}
                          onClick={() => setTab("users")}
                        >
                          Åpne i brukerliste
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: "20px" }}>
                <h4 style={{ margin: "0 0 14px", color: C.primary }}>
                  Kjøretøy i firmaet
                </h4>
                {selectedCompanyVehicles.length === 0 ? (
                  <div
                    style={{
                      padding: "18px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.03)",
                      color: C.darkGray,
                    }}
                  >
                    Ingen kjøretøy er registrert på dette firmaet ennå.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {selectedCompanyVehicles.slice(0, 10).map((vehicle) => (
                      <div
                        key={vehicle.id}
                        className="card"
                        style={{
                          padding: "12px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {(vehicle.basicInfo && vehicle.basicInfo.regNum) || "Ukjent reg.nr"}
                          </div>
                          <div style={{ color: C.darkGray, fontSize: "13px", marginTop: "2px" }}>
                            {vehicle.type === "return" ? "Retur" : "Mottak"}
                          </div>
                        </div>
                        <span className={vehicle.focalx ? "status-ok" : "status-bad"}>
                          {vehicle.focalx ? "Focalx" : "Uten Focalx"}
                        </span>
                      </div>
                    ))}
                    {selectedCompanyVehicles.length > 10 && (
                      <div style={{ color: C.darkGray, fontSize: "13px" }}>
                        Viser 10 av {selectedCompanyVehicles.length} kjøretøy.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card" style={{ padding: "28px", marginBottom: "24px" }}>
            <h3 style={{ margin: "0 0 20px", color: C.primary }}>
              Legg til bedrift
            </h3>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <input
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCompany()}
                  placeholder="Bedriftsnavn"
                  className="inp"
                />
              </div>
              <button
                onClick={handleAddCompany}
                disabled={savingCompany}
                className="btn-p"
                style={{ whiteSpace: "nowrap" }}
              >
                {savingCompany ? "Lagrer..." : "+ Legg til"}
              </button>
            </div>
            {companyError && (
              <p style={{ color: C.danger, margin: "10px 0 0" }}>
                {companyError}
              </p>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {companies.length === 0 ? (
              <div
                className="card"
                style={{ padding: "48px", textAlign: "center", color: C.darkGray }}
              >
                Ingen bedrifter ennå
              </div>
            ) : (
              companies.map((c) => (
                <div
                  key={c.id}
                  className="card"
                  style={{
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                  onClick={() => openCompany(c.id)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        background:
                          "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                      }}
                    >
                      🏢
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "17px",
                          color: C.text,
                        }}
                      >
                        {c.name}
                      </div>
                      <div style={{ color: C.darkGray, fontSize: "13px", marginTop: "2px" }}>
                        Opprettet:{" "}
                        {new Date(c.created_at).toLocaleDateString("nb-NO")}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <span className="status-ok">
                      👤 {c.user_count} bruker{c.user_count !== 1 ? "e" : ""}
                    </span>
                    <span
                      style={{
                        background: "rgba(102,126,234,0.2)",
                        color: "#667eea",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: "600",
                        border: "1px solid rgba(102,126,234,0.3)",
                      }}
                    >
                      🚗 {c.vehicle_count} kjøretøy
                    </span>
                    <button
                      className="btn-s"
                      style={{ padding: "6px 12px", fontSize: "13px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCompany(c.id);
                      }}
                    >
                      Åpne
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
            <button
              onClick={() => {
                setShowAddUser((v) => !v);
                setUserError("");
              }}
              className="btn-p"
            >
              {showAddUser ? "Avbryt" : "+ Ny bruker"}
            </button>
          </div>

          {showAddUser && (
            <div
              className="card"
              style={{ padding: "28px", marginBottom: "24px" }}
            >
              <h3 style={{ margin: "0 0 20px", color: C.primary }}>
                Opprett bruker
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    E-post *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    className="inp"
                    placeholder="bruker@firma.no"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Fullt navn *
                  </label>
                  <input
                    value={newUser.fullName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, fullName: e.target.value })
                    }
                    className="inp"
                    placeholder="Ola Nordmann"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Passord *
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    className="inp"
                    placeholder="Minst 8 tegn"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Bedrift *
                  </label>
                  <select
                    value={newUser.companyId}
                    onChange={(e) =>
                      setNewUser({ ...newUser, companyId: e.target.value })
                    }
                    className="inp"
                  >
                    <option value="">Velg bedrift...</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Rolle
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                    className="inp"
                  >
                    <option value="user">Bruker</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              {userError && (
                <p style={{ color: C.danger, margin: "12px 0 0" }}>
                  {userError}
                </p>
              )}
              <div style={{ marginTop: "20px" }}>
                <button
                  onClick={handleAddUser}
                  disabled={savingUser}
                  className="btn-p"
                >
                  {savingUser ? "Oppretter..." : "Opprett bruker"}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {users.length === 0 ? (
              <div
                className="card"
                style={{
                  padding: "48px",
                  textAlign: "center",
                  color: C.darkGray,
                }}
              >
                Ingen brukere ennå
              </div>
            ) : (
              users.map((u) => (
                <div
                  key={u.id}
                  className="card"
                  style={{
                    padding: "18px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        background:
                          u.role === "admin"
                            ? "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)"
                            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "700",
                        fontSize: "16px",
                        color: "#0a0a0a",
                        flexShrink: 0,
                      }}
                    >
                      {(u.full_name || u.email)[0].toUpperCase()}
                    </div>
                    <div>
                      {editingUserId === u.id ? (
                        <div style={{ display: "grid", gap: "8px", minWidth: "280px" }}>
                          <input
                            className="inp"
                            value={editUserDraft.email}
                            onChange={(e) => setEditUserDraft({ ...editUserDraft, email: e.target.value })}
                            placeholder="E-post"
                          />
                          <input
                            className="inp"
                            value={editUserDraft.fullName}
                            onChange={(e) => setEditUserDraft({ ...editUserDraft, fullName: e.target.value })}
                            placeholder="Fullt navn"
                          />
                          <select
                            className="inp"
                            value={editUserDraft.companyId}
                            onChange={(e) => setEditUserDraft({ ...editUserDraft, companyId: e.target.value })}
                          >
                            <option value="">Velg bedrift...</option>
                            {companies.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                            <select
                              className="inp"
                              value={editUserDraft.role}
                              onChange={(e) => setEditUserDraft({ ...editUserDraft, role: e.target.value })}
                            >
                              <option value="user">Bruker</option>
                              <option value="admin">Admin</option>
                            </select>
                            <input
                              className="inp"
                              type="password"
                              value={editUserDraft.password}
                              onChange={(e) => setEditUserDraft({ ...editUserDraft, password: e.target.value })}
                              placeholder="Nytt passord (valgfritt)"
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontWeight: "600", fontSize: "15px" }}>
                            {u.full_name}
                          </div>
                          <div
                            style={{
                              color: C.darkGray,
                              fontSize: "13px",
                              marginTop: "2px",
                            }}
                          >
                            {u.email} · {" "}
                            <span
                              style={{ cursor: "pointer", textDecoration: "underline" }}
                              onClick={() => {
                                const company = companies.find((c) => c.id === u.company_id);
                                if (company) {
                                  setTab("companies");
                                  openCompany(company.id);
                                }
                              }}
                            >
                              {u.company_name}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {u.role === "admin" ? (
                      <span className="status-ok">● Admin</span>
                    ) : (
                      <span
                        style={{
                          background: "rgba(102,126,234,0.15)",
                          color: "#667eea",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          border: "1px solid rgba(102,126,234,0.3)",
                        }}
                      >
                        Bruker
                      </span>
                    )}
                    {editingUserId === u.id ? (
                      <>
                        <button
                          onClick={() => handleUpdateUser(u.id)}
                          className="btn-p"
                          style={{ padding: "8px 16px", fontSize: "13px" }}
                          disabled={savingEditUser}
                        >
                          {savingEditUser ? "Lagrer..." : "Lagre"}
                        </button>
                        <button
                          onClick={cancelEditUser}
                          className="btn-s"
                          style={{ padding: "8px 16px", fontSize: "13px" }}
                        >
                          Avbryt
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEditUser(u)}
                        className="btn-s"
                        style={{ padding: "8px 16px", fontSize: "13px" }}
                      >
                        Rediger
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(u.id, u.full_name)}
                      className="btn-d"
                      style={{ padding: "8px 16px", fontSize: "13px" }}
                    >
                      Slett
                    </button>
                  </div>
                  {editingUserId === u.id && editUserError && (
                    <p style={{ color: C.danger, width: "100%", margin: "0" }}>
                      {editUserError}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
